import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons/faAngleDown";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import { MenuDividerType } from "antd/es/menu/hooks/useItems";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { sortBy, uniq } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";

import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button, Dropdown, NumberField, NumberFieldProps } from "@mapmycustomers/ui";

import { setDistanceSettings } from "@app/component/leadFinder/store/actions";
import {
  getLeadFinderCustomDistances,
  getLeadFinderSearchDistance,
} from "@app/component/leadFinder/store/selectors";
import {
  LEAD_FINDER_CUSTOM_DISTANCES_MAX,
  LEAD_FINDER_DEFAULT_DISTANCE_KM,
  LEAD_FINDER_DEFAULT_DISTANCE_MILES,
  LEAD_FINDER_DISTANCES_KM,
  LEAD_FINDER_DISTANCES_MILES,
} from "@app/component/leadFinder/utils/consts";
import localSettings from "@app/config/LocalSettings";
import { RootState } from "@app/store/rootReducer";
import roundToPrecision from "@app/util/number/roundToPrecision";

import styles from "./SearchRadius.module.scss";

interface Props {
  classes?: {
    dropdown?: string;
    location?: string;
  };
  customDistances: Array<number>;
  radius: number;
  setDistanceSettings: typeof setDistanceSettings;
}

const messages = defineMessages({
  addItem: {
    id: "leadFinder.searchPopover.searchRadius.addItem",
    defaultMessage: "Add item",
    description: "Add Item button title",
  },
  alreadyInList: {
    id: "leadFinder.searchPopover.searchRadius.alreadyInList",
    defaultMessage: "Distance already available on list",
    description: "Distance already available on list",
  },
  distanceLimit: {
    id: "leadFinder.searchPopover.searchRadius.distanceLimit",
    defaultMessage: "Distance must be less than {distance}",
    description: "Distance must be less than X",
  },
  km: {
    id: "leadFinder.searchPopover.searchRadius.km",
    defaultMessage: "{distance} km",
    description: "Kilometer unit in custom distance menu",
  },
  limit: {
    id: "leadFinder.searchPopover.searchRadius.limit",
    defaultMessage: "Cannot add more than {num} custom distances",
    description: "Cannot add more than X custom distances",
  },
  miles: {
    id: "leadFinder.searchPopover.searchRadius.miles",
    defaultMessage: "{distance} {distance, plural, =0 {miles} one {mile} other {miles}}",
    description: "Mile unit in custom distance menu",
  },
  placeholder: {
    id: "leadFinder.searchPopover.searchRadius.placeholder",
    defaultMessage: "< {distance}",
    description: "Placeholder for distance input",
  },
  unitKm: {
    id: "leadFinder.searchPopover.searchRadius.unit.km",
    defaultMessage: "km",
    description: "Kilometer unit in custom distance menu",
  },
  unitMiles: {
    id: "leadFinder.searchPopover.searchRadius.unit.miles",
    defaultMessage: "miles",
    description: "Mile unit in custom distance menu",
  },
});

const SearchRadius = ({ classes, customDistances, radius, setDistanceSettings }: Props) => {
  const intl = useIntl();

  const [value, setValue] = useState<number | undefined>();

  const doesUseSiUnits = localSettings.doesUseSiUnits();
  const defaultDistances = doesUseSiUnits ? LEAD_FINDER_DISTANCES_KM : LEAD_FINDER_DISTANCES_MILES;

  const maxValue = doesUseSiUnits
    ? LEAD_FINDER_DEFAULT_DISTANCE_KM
    : LEAD_FINDER_DEFAULT_DISTANCE_MILES;

  const distanceFormatMessage = doesUseSiUnits ? messages.km : messages.miles;
  const unitMessage = doesUseSiUnits ? messages.unitKm : messages.unitMiles;

  const errorValueMissing = value === undefined || isNaN(value);
  const errorValueTooBig = !errorValueMissing && value > maxValue;
  const errorValueOutOfRange = !errorValueMissing && (value < 0.1 || errorValueTooBig);
  const errorValueIncorrect = errorValueMissing || errorValueOutOfRange;
  const errorValueAlreadyInList = useMemo(
    () => [...customDistances, ...defaultDistances].includes(value ?? 0),
    [customDistances, defaultDistances, value]
  );
  const errorLimitValues = customDistances?.length >= LEAD_FINDER_CUSTOM_DISTANCES_MAX;

  const errorMessage = errorValueTooBig
    ? intl.formatMessage(messages.distanceLimit, {
        distance: intl.formatMessage(distanceFormatMessage, { distance: maxValue }),
      })
    : errorValueAlreadyInList
      ? intl.formatMessage(messages.alreadyInList)
      : errorLimitValues
        ? intl.formatMessage(messages.limit, { num: LEAD_FINDER_CUSTOM_DISTANCES_MAX })
        : null;

  const hasCustomDistances = customDistances?.length > 0;
  const addBtnDisabled = errorValueIncorrect || errorValueAlreadyInList || errorLimitValues;

  const handleClick = useCallback<NonNullable<MenuProps["onClick"]>>(
    (value) => {
      setDistanceSettings({ searchDistance: parseFloat(value.key) });
      setValue(undefined);
    },
    [setDistanceSettings, setValue]
  );

  const handleDistanceChange = useCallback(
    (value: number | undefined) => {
      if (value !== undefined) {
        setValue(roundToPrecision(value, 1));
      }
    },
    [setValue]
  );

  const handleAdd = useCallback(() => {
    if (!addBtnDisabled) {
      setDistanceSettings({
        customDistances: sortBy(uniq([...customDistances, value])),
        searchDistance: value,
      });
    }

    setValue(undefined);
  }, [addBtnDisabled, customDistances, setDistanceSettings, setValue, value]);

  const handleKeyPress = useCallback<NonNullable<NumberFieldProps["onKeyPress"]>>(
    (event) => {
      if (!addBtnDisabled && event.key === "Enter") {
        stopEvents(event);
        handleAdd();
      }
    },
    [addBtnDisabled, handleAdd]
  );

  const handleRemove = useCallback(
    (distance: number) => {
      const updatedList = sortBy(uniq(customDistances.filter((value) => value !== distance)));
      const newDistance = updatedList.find((value) => value > distance) ?? maxValue;

      const updatedSettings = {
        customDistances: updatedList,
        ...(distance === radius ? { searchDistance: newDistance } : {}),
      };

      setDistanceSettings(updatedSettings);
      setValue(undefined);
    },
    [customDistances, maxValue, radius, setDistanceSettings, setValue]
  );

  return (
    <Dropdown
      dropdownRender={(menu) => (
        <div className={styles.overlay}>
          {menu}

          <div className={styles.custom} onClick={stopEvents}>
            <div className={styles.group}>
              <NumberField
                bordered={false}
                inputClassName={cn({ [styles.error]: errorValueOutOfRange })}
                min={0.1}
                onChange={handleDistanceChange}
                onKeyDown={handleKeyPress}
                placeholder={intl.formatMessage(messages.placeholder, { distance: maxValue })}
                size="middle"
                value={value}
              />
              <span className={styles.unit}>{intl.formatMessage(unitMessage)}</span>
            </div>

            <Tooltip overlayClassName={styles.tooltip} title={errorMessage}>
              <div>
                <Button
                  disabled={addBtnDisabled}
                  leftIcon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={handleAdd}
                  type="control"
                >
                  {intl.formatMessage(messages.addItem)}
                </Button>
              </div>
            </Tooltip>
          </div>
        </div>
      )}
      menu={{
        className: styles.menu,
        items: [
          ...defaultDistances.map((distance) => ({
            key: distance,
            label: intl.formatMessage(distanceFormatMessage, { distance }),
          })),
          ...(hasCustomDistances
            ? [
                { key: "divider", type: "divider" as MenuDividerType["type"] },
                ...customDistances.map((distance) => ({
                  className: styles.menuItem,
                  key: distance,
                  label: (
                    <>
                      {intl.formatMessage(distanceFormatMessage, { distance })}
                      <Button
                        className={styles.remove}
                        icon={<FontAwesomeIcon icon={faMinusCircle} />}
                        onClick={(event) => {
                          stopEvents(event);
                          handleRemove(distance);
                        }}
                        type="link"
                      />
                    </>
                  ),
                })),
              ]
            : []),
        ],
        onClick: handleClick,
        selectedKeys: [String(radius)],
      }}
      overlayClassName={styles.container}
      placement="bottomLeft"
      trigger={["click"]}
    >
      <Button className={cn(styles.btn, classes?.dropdown)} type="text">
        {intl.formatMessage(distanceFormatMessage, { distance: radius })}
        <FontAwesomeIcon className={styles.arrow} icon={faAngleDown} />
      </Button>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  customDistances: getLeadFinderCustomDistances(state),
  radius: getLeadFinderSearchDistance(state),
});

const mapDispatchToProps = {
  setDistanceSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchRadius);
